import { useAuth } from '@omnipresentgroup/auth';
import { HTTPStatusCodes } from '@omnipresentgroup/constants';
import {
  type ErrorResponse,
  isErrorResponse,
  isLegacyErrorResponse,
  type LegacyErrorResponse,
  type Problem,
} from '@omnipresentgroup/types';
import wretch, { type WretchError, type WretchOptions } from 'wretch';
import FormDataAddon from 'wretch/addons/formData';
import QueryStringAddon from 'wretch/addons/queryString';

const CONTENT_TYPE_HEADER = 'Content-Type';
const CONTENT_LENGTH_HEADER = 'Content-Length';
const JSON_CONTENT_TYPE = 'application/json';

type Headers = Record<string, string>;

type Queries = Record<string, string | string[]>;

export class LegacyApiError extends Error {
  status: number;
  detail?: string;

  constructor(error: LegacyErrorResponse) {
    super(typeof error.message === 'string' ? error.message : '');

    // Ensure the name of this error is the same as the class name
    this.name = this.constructor.name;
    this.detail = error.message;
    this.status = error.status ?? error.statusCode!;

    if (Error.captureStackTrace) {
      // This clips the constructor invocation from the stack trace.
      // It's not absolutely essential, but it does make the stack trace a little nicer.
      Error.captureStackTrace(this, this.constructor);
    }
  }

  get stringErrors(): string[] {
    return [this.message];
  }
}

export class ApiError extends Error {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  problems?: Problem[];
  metadata?: Record<string, unknown>;
  docs?: string;

  constructor(error: ErrorResponse) {
    super(error.title);

    // Ensure the name of this error is the same as the class name
    this.name = this.constructor.name;

    this.type = error.type;
    this.title = error.title;
    this.status = error.status;
    this.detail = error.detail;
    this.instance = error.instance;
    this.traceId = error.traceId;
    this.problems = error.problems;
    this.metadata = error.metadata;
    this.docs = error.docs;

    if (Error.captureStackTrace) {
      // This clips the constructor invocation from the stack trace.
      // It's not absolutely essential, but it does make the stack trace a little nicer.
      Error.captureStackTrace(this, this.constructor);
    }
  }

  get stringErrors(): string[] {
    if (this.problems?.length) {
      return this.problems.map((problem) =>
        [
          problem.title,
          problem.detail,
          (problem.metadata?.details as { loc: string[] })?.loc?.join(' > '),
        ]
          .filter(Boolean)
          .join('. '),
      );
    }

    if (this.title) {
      return [`${this.detail ?? this.title}`];
    }

    return [];
  }
}

export const errorCatcher = (error: WretchError) => {
  if (error.json) {
    if (isLegacyErrorResponse(error.json)) {
      throw new LegacyApiError(error.json);
    }
    if (isErrorResponse(error.json)) {
      throw new ApiError(error.json);
    }
  }
  throw new Error('Invalid API response format');
};

export const fetch = (
  path: string,
  options: WretchOptions = {},
  headers: Headers = {},
  queries: Queries = {},
) => {
  const wretchInstance = wretch(path, options)
    .addon(FormDataAddon)
    .addon(QueryStringAddon)
    .headers(headers)
    .query(queries)
    .errorType('json')
    .catcher(HTTPStatusCodes.BAD_REQUEST, errorCatcher)
    .catcher(HTTPStatusCodes.NOT_FOUND, errorCatcher)
    .catcher(HTTPStatusCodes.CONFLICT, errorCatcher)
    .catcher(HTTPStatusCodes.UNSUPPORTED_MEDIA_TYPE, errorCatcher)
    .catcher(HTTPStatusCodes.UNPROCESSABLE, errorCatcher)
    .catcher(HTTPStatusCodes.SERVER_ERROR, errorCatcher)
    .catcher(HTTPStatusCodes.SERVICE_UNAVAILABLE, errorCatcher)
    .catcher(HTTPStatusCodes.TOO_MANY_REQUESTS, errorCatcher);

  return wretchInstance;
};

export const useApi = () => {
  const { getAccessTokenSilently } = useAuth();

  const fetchWithToken = async ({
    path,
    options = {},
    queries = {},
    headers = {},
  }: {
    path: string;
    options?: WretchOptions;
    queries?: Queries;
    headers?: Headers;
  }) => {
    const token = await getAccessTokenSilently();
    const wretchInstance = fetch(
      path,
      options,
      {
        Authorization: `Bearer ${token}`,
        ...headers,
      },
      queries,
    );
    return wretchInstance;
  };

  const GET = async <T>(
    path: string,
    options?: WretchOptions,
    queries?: Queries,
  ) => {
    const fetcher = await fetchWithToken({
      path,
      options,
      queries,
      headers: {
        [CONTENT_TYPE_HEADER]: JSON_CONTENT_TYPE,
      },
    });
    const response = await fetcher.get().res();

    const contentType = response.headers.get(CONTENT_TYPE_HEADER);

    if (contentType?.includes(JSON_CONTENT_TYPE)) {
      return response.json() as Promise<T>;
    }

    return response as T;
  };

  const POST = async <T>({
    path,
    body = {},
    options = {},
    headers = {},
    asFormData = false,
  }: {
    path: string;
    body?: Record<string, unknown> | FormData;
    options?: WretchOptions;
    headers?: Headers;
    asFormData?: boolean;
  }) => {
    // Make sure we never send the Content-Type header when sending FormData
    const { 'Content-Type': _, ...nonContentTypeHeaders } = headers;

    const fetcher = await fetchWithToken({
      path,
      options,
      headers: asFormData
        ? nonContentTypeHeaders
        : { [CONTENT_TYPE_HEADER]: JSON_CONTENT_TYPE, ...headers },
    });

    const response = await (asFormData
      ? fetcher.formData(body).post().res()
      : fetcher.post(body).res());

    const isEmptyContent =
      response.status === HTTPStatusCodes.NO_CONTENT ||
      response.headers.get(CONTENT_LENGTH_HEADER) === '0';

    return isEmptyContent
      ? (Promise.resolve({}) as Promise<T>)
      : (response.json() as Promise<T>);
  };

  const PUT = async <T>({
    path,
    body = {},
    options = {},
    headers = {},
  }: {
    path: string;
    body?: Record<string, unknown>;
    options?: WretchOptions;
    headers?: Headers;
  }) => {
    const fetcher = await fetchWithToken({
      path,
      options,
      headers: { [CONTENT_TYPE_HEADER]: JSON_CONTENT_TYPE, ...headers },
    });
    return fetcher.put(body).json<T>();
  };

  const PATCH = async ({
    path,
    body = {},
    options = {},
    headers = {},
  }: {
    path: string;
    body?: Record<string, unknown>;
    options?: WretchOptions;
    headers?: Headers;
  }) => {
    const fetcher = await fetchWithToken({
      path,
      options,
      headers: { [CONTENT_TYPE_HEADER]: JSON_CONTENT_TYPE, ...headers },
    });
    return fetcher.patch(body).res();
  };

  const DELETE = async (path: string, options?: WretchOptions) => {
    const fetcher = await fetchWithToken({
      path,
      options,
      headers: {
        [CONTENT_TYPE_HEADER]: JSON_CONTENT_TYPE,
      },
    });
    return fetcher.delete().res();
  };

  return { fetchWithToken, GET, POST, PUT, PATCH, DELETE };
};
